import { useEffect, useState } from 'react'
import { useAuth } from '../context/useAuth'
import { useWorkspace } from '../context/useWorkspace'
import { isWorkspaceRole, roleCanPerform } from '../utils/workspacePermissions'
import { subscribeToMyScopeMode } from '../services/accessScopeService'
import { getHelperSnapshot } from '../services/helper/helperStore'
import { isHelperOnlyAccount } from '../utils/helperMembership'

export interface MyAccessScope {
  restricted: boolean
  helper: boolean
  ready: boolean
}

export const useMyAccessScope = (
  workspaceId?: string | null,
  options?: { isPublicReadOnly?: boolean },
): MyAccessScope => {
  const { currentUser, userProfile } = useAuth()
  const { activeRole, workspaces } = useWorkspace()

  const isPublicReadOnly = options?.isPublicReadOnly === true
  const activeWorkspace = workspaces?.find((w) => w.id === workspaceId)
  const isPreview = activeWorkspace?.isPreview === true
  const isAdmin = Boolean(activeRole && isWorkspaceRole(activeRole) && roleCanPerform(activeRole, 'ADMIN'))

  // In a helper session, the HELPER role comes from either userProfile.role (src/main.tsx:458),
  // isPhoneOnlyIdentity(currentUser),
  // useWorkspace().activeRole (WorkspaceContext.tsx:1106 via workspaceRoleResolution.ts:30)
  // or helperStore (helperStore.ts:29, helperWorkspaceService.ts:106/135).
  const isHelper = Boolean(
    isHelperOnlyAccount({ userProfile, currentUser, workspaces, activeRole }) ||
    activeRole === 'HELPER' ||
    (workspaceId && getHelperSnapshot().workspaces.some((w) => w.id === workspaceId && w.role === 'HELPER')),
  )

  const isExempt = isAdmin || isPublicReadOnly || isPreview || isHelper
  const canSubscribe = !isExempt && Boolean(workspaceId && currentUser?.uid)
  const currentKey = workspaceId && currentUser?.uid ? `${workspaceId}:${currentUser.uid}` : null

  const [storedScope, setStoredScope] = useState<{ key: string; restricted: boolean } | null>(null)

  useEffect(() => {
    if (!canSubscribe || !workspaceId || !currentUser?.uid) {
      return
    }

    const scopeKey = `${workspaceId}:${currentUser.uid}`
    const unsubscribe = subscribeToMyScopeMode(
      workspaceId,
      currentUser.uid,
      (mode) => {
        setStoredScope({
          key: scopeKey,
          restricted: mode === 'RESTRICTED',
        })
      },
      () => {
        // On a snapshot error: ready, not restricted (a missing mirror makes the rules treat the member as unrestricted).
        setStoredScope({
          key: scopeKey,
          restricted: false,
        })
      },
    )

    return () => {
      unsubscribe()
    }
  }, [canSubscribe, currentUser?.uid, workspaceId])

  if (isHelper) {
    return { restricted: false, helper: true, ready: true }
  }

  if (!canSubscribe) {
    return { restricted: false, helper: false, ready: true }
  }

  if (storedScope && currentKey && storedScope.key === currentKey) {
    return { restricted: storedScope.restricted, helper: false, ready: true }
  }

  return { restricted: false, helper: false, ready: false }
}
